import {
  Anchor,
  AspectRatio,
  BackgroundImage,
  Box,
  Button,
  Card,
  Center,
  Flex,
  Group,
  Image,
  Overlay,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  useMantineColorScheme,
  useMatches,
} from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import excellentDark from "../../../assets/illustrations/conditions/excellent-dark.svg";
import excellent from "../../../assets/illustrations/conditions/excellent.svg";
import fairDark from "../../../assets/illustrations/conditions/fair-dark.svg";
import fair from "../../../assets/illustrations/conditions/fair.svg";
import goodDark from "../../../assets/illustrations/conditions/good-dark.svg";
import good from "../../../assets/illustrations/conditions/good.svg";
import superbDark from "../../../assets/illustrations/conditions/superb-dark.svg";
import superb from "../../../assets/illustrations/conditions/superb.svg";
import { DataGrid } from "../DataGrid";
import { MakeCard } from "../MakeCard";
import { Modal } from "../Modal";
import { PlaceholderValue } from "../PlaceholderValue";
import { TextSerif } from "../TextSerif";
import { TransactionStatusBadge } from "../TransactionStatusBadge";
import classes from "./AuctionCarCard.module.css";
import { calculateEstimate } from "./utils";

import carburateLogo from "@/assets/illustrations/carburate-logo-small.svg";
import { defaultCurrency } from "@/lib/currency";
import { formatShortDateWithTime } from "@/utils/formatDate";
import { formatCurrency } from "@/utils/formatEuro";
import { getTransactionNotAvailableLabel } from "@/utils/transactionRecord";

import type { AuctionCar } from "@/types/auctions";
import type { Currency } from "@/types/currency";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";
import type { LabelValueProps } from "../LabelValue";

export type AuctionCarCardProps = {
  car: AuctionCar;
  loading?: boolean;
  to?: string;
} & MantineStyleProps;

export const AuctionCarCard: FC<AuctionCarCardProps> = ({ car, loading = false, to, ...props }) => {
  const { t } = useTranslation();
  const isMobile = useMatches({ base: true, sm: false });
  const [opened, { open, close }] = useDisclosure(false);
  const ratio = useMatches({ base: 343 / 120, sm: 240 / 160 }); // aspect ratio coming from figma
  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });
  const [showDescription, setShowDescription] = useState(false);

  const hasLink = !!car.taxonomy_id && !!to;

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const textColor = isDark ? "var(--mantine-color-primary-2)" : "var(--mantine-color-primary-7)";

  const renderCondition = (conditionVal?: any) => {
    const cond = String(conditionVal).toLowerCase();

    if (cond === "not_available" || cond === "-1") {
      return (
        <Flex align="center" style={{ width: "100%", height: "100%" }}>
          <Text fz="sm">{String(t(`car.conditions.not_available`, "not_available"))}</Text>
        </Flex>
      );
    }

    const imgSrc =
      cond === "superb"
        ? isDark
          ? superbDark
          : superb
        : cond === "excellent"
          ? isDark
            ? excellentDark
            : excellent
          : cond === "good"
            ? isDark
              ? goodDark
              : good
            : cond === "fair"
              ? isDark
                ? fairDark
                : fair
              : undefined;

    if (!imgSrc) {
      return (
        <Flex align="center" style={{ width: "100%", height: "100%" }}>
          <Text tt="uppercase">{String(t(`car.conditions.${conditionVal}`, conditionVal))}</Text>
        </Flex>
      );
    }

    return (
      <Flex direction="row" justify="space-between" align="center" gap="md" w="fit">
        <Text fz="sm" miw={0} style={{ flex: 1 }} truncate>
          {String(t(`car.conditions.${conditionVal}`, conditionVal))}
        </Text>
        <AspectRatio
          w="2em"
          style={{ display: "flex", alignItems: "center" }}
          pos="relative"
          bottom={2}
        >
          <Image
            src={imgSrc}
            fit="contain"
            alt={String(t(`car.conditions.${cond}`, cond))}
            aria-label={String(t(`car.conditions.${cond}`, cond))}
            style={{ display: "block", margin: "auto" }}
          />
        </AspectRatio>
      </Flex>
    );
  };

  const estimante = calculateEstimate(car, currency);
  const carburateEstimate = calculateEstimate(car, currency, "carburate");
  const notAvailable = getTransactionNotAvailableLabel(t, false);

  const data1: LabelValueProps[] = [
    {
      label: t("components.AuctionCarCard.lot"),
      value: car.lot === "0" ? "1" : car.lot, // can't never be 0, if 0 is returned we set it to 1
    },
    {
      label: t("components.AuctionCarCard.vin"),
      value:
        car.vin && car.vin !== "-" ? (
          hasLink ? (
            <Anchor component={Link} fw={500} to={to}>
              {car.vin}
            </Anchor>
          ) : (
            car.vin
          )
        ) : (
          <PlaceholderValue w={100} lines={1} />
        ),
    },
    {
      label: t("components.AuctionCarCard.condition"),
      value:
        car?.condition !== undefined ? (
          renderCondition(car.condition)
        ) : (
          <PlaceholderValue w={100} lines={1} />
        ),
      infoIcon: String(t("tooltips.condition")),
    },
  ];

  const data2: LabelValueProps[] = [
    {
      label: t("components.AuctionCarCard.estimate"),
      value: estimante,
    },
    {
      label: t("components.AuctionCarCard.carburate_estimate"),
      value: carburateEstimate,
      c: carburateEstimate !== "-" ? "success" : undefined,
    },
    {
      label: t("components.AuctionCarCard.hammer_price"),
      value:
        car.hammer_price !== undefined ? (
          formatCurrency(car.hammer_price, currency.code)
        ) : (
          <PlaceholderValue w={80} lines={1} />
        ),
    },
    {
      label: t("components.AuctionCarCard.net_to_seller"),
      value:
        car.net_to_seller !== undefined ? (
          formatCurrency(car.net_to_seller, currency.code)
        ) : (
          <PlaceholderValue w={80} lines={1} />
        ),
      infoIcon: String(t("tooltips.netToSeller")),
    },
    {
      label: "",
      value: (
        <Box
          component="span"
          role="button"
          tabIndex={0}
          onClick={() => setShowDescription((s) => !s)}
          aria-label={String(t("components.AuctionCarCard.view_description"))}
          display="inline-block"
          style={{ cursor: "pointer" }}
        >
          {car.description_summary && (
            <Text fz="md" c={textColor}>
              {showDescription
                ? t("components.AuctionCarCard.hide_description")
                : t("components.AuctionCarCard.view_description")}
            </Text>
          )}
        </Box>
      ),
    },
  ];

  const dataMobile: LabelValueProps[] = [
    {
      label: t("components.AuctionCarCard.lot"),
      value: car.lot === "0" ? "1" : car.lot, // can't never be 0, if 0 is returned we set it to 1
    },
    {
      label: t("components.AuctionCarCard.condition"),
      value: car?.condition ? renderCondition(car.condition) : null,
    },
  ];

  const bgColor = isDark ? "#323232" : "#d9d9d9";

  // Close modal when window is resized (to avoid layout issues)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => close();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [close]);

  return (
    <Card {...props} className={classes.card}>
      <Skeleton visible={loading}>
        <Card.Section>
          <Group justify="space-between" align="flex-start" gap={0}>
            <AspectRatio
              ratio={ratio}
              miw={240}
              w={{ base: "100%", sm: "25%" }}
              pos="relative"
              onClick={isMobile ? open : undefined}
            >
              {car.image ? (
                <BackgroundImage src={car.image} maw="100%" mih={160} />
              ) : (
                /* Car Image Placeholder */
                <Center bg={bgColor} bdrs="md" h="100%">
                  <Image src={carburateLogo} w="auto" height="60%" alt="Carburate" />
                </Center>
              )}

              <Overlay backgroundOpacity={0} h="100%">
                <Stack justify="space-between" h="100%" p="lg">
                  <Group justify="space-between" align="flex-start">
                    <MakeCard
                      w={{ base: 34, sm: 40 }}
                      make={car.make_name}
                      FallbackComponent={<Box />}
                    />
                  </Group>
                  {/* <Group justify="space-between" hiddenFrom="sm">
                    <TransactionStatusBadge status={car.status} size="lg" />
                  </Group> */}
                </Stack>
              </Overlay>
            </AspectRatio>
            <Stack flex={1} gap="sm" py="md" px="lg" visibleFrom="sm">
              <Group justify="space-between" align="center">
                <Group gap="md">
                  <TransactionStatusBadge status={car.status} />
                  {hasLink ? (
                    <Anchor component={Link} fw={500} to={to}>
                      {car.make_name} {car.model_name}
                    </Anchor>
                  ) : (
                    <Text fw={500}>
                      {car.make_name} {car.model_name}
                    </Text>
                  )}
                </Group>
                <Group gap="md">
                  <TextSerif fz="sm">{formatShortDateWithTime(car.updated_at)}</TextSerif>
                </Group>
              </Group>
              <DataGrid cols={4} items={data1} />
              <DataGrid cols={4} items={data2} />
            </Stack>
          </Group>
        </Card.Section>

        {/* Mobile: show basic info and data under the image */}
        <Card.Section inheritPadding hiddenFrom="sm">
          <Stack gap="sm" py="md">
            <Group justify="space-between" align="center">
              <Group gap="md">
                {hasLink ? (
                  <Anchor component={Link} fw={500} to={to} fz="lg">
                    {car.make_name} {car.model_name}
                  </Anchor>
                ) : (
                  <Text fw={500} fz="lg">
                    {car.make_name} {car.model_name}
                  </Text>
                )}
              </Group>
              <Group gap="md">
                <TextSerif fz="sm">{formatShortDateWithTime(car.updated_at)}</TextSerif>
              </Group>
            </Group>

            <Stack gap={4}>
              {dataMobile.map((item, i) => (
                <Group key={i} align="center" style={{ width: "100%" }}>
                  <Text c="dimmed" fz="sm" style={{ flex: 1 }}>
                    {item.label}
                  </Text>
                  <Box
                    style={{
                      flex: "0 0 auto",
                      display: "flex",
                      justifyContent: "flex-end",
                      minWidth: 0,
                    }}
                  >
                    {item.value}
                  </Box>
                </Group>
              ))}
            </Stack>
          </Stack>
        </Card.Section>

        <Card.Section inheritPadding>
          {showDescription && car.description_summary && (
            <>
              <Box visibleFrom="sm" py="md" px="sm">
                <TextSerif size="sm">
                  {t("components.AuctionCarCard.description_summary")}
                </TextSerif>
                <Text fz="sm">{car.description_summary}</Text>
              </Box>
              <Stack gap="md" hiddenFrom="sm">
                {hasLink ? (
                  <Anchor component={Link} fw={500} to={to}>
                    {car.make_name} {car.model_name}
                  </Anchor>
                ) : (
                  <Text fw={500}>
                    {car.make_name} {car.model_name}
                  </Text>
                )}
                <Flex>
                  <Stack>
                    <TextSerif size="sm">
                      {t("components.AuctionCarCard.description_summary")}
                    </TextSerif>
                    <Text fz="sm">{car.description_summary}</Text>
                  </Stack>
                  <Stack></Stack>
                </Flex>
              </Stack>
            </>
          )}
        </Card.Section>
      </Skeleton>

      <Modal title={t("components.Auctions.details")} opened={opened} onClose={close}>
        <Stack gap="md" style={{ display: "flex", flexDirection: "column", height: "85vh" }}>
          <Box style={{ flex: 1, overflowY: "auto" }}>
            {/* Image */}
            <AspectRatio
              ratio={343 / 160}
              pos="relative"
              style={{ overflow: "hidden", borderRadius: "0.5rem" }}
            >
              {/*             {car.image ? (
              <BackgroundImage src={car.image} maw="100%" mih={160} />
            ) : (
              <Center bg={bgColor} bdrs="md" h="100%">
                <Image src={carburateLogo} w="auto" height="60%" alt="Carburate" />
              </Center>
            )} */}
              <Overlay backgroundOpacity={0} h="100%">
                <Stack justify="space-between" h="100%" p="lg">
                  <Group justify="space-between" align="flex-start">
                    <MakeCard w={34} make={car.make_name} FallbackComponent={<Box />} />
                  </Group>
                  <Group>
                    <TransactionStatusBadge status={car.status} size="lg" />
                  </Group>
                </Stack>
              </Overlay>
            </AspectRatio>

            {/* Car title */}
            {hasLink ? (
              <Anchor component={Link} fw={500} fz="lg" to={to} c={textColor}>
                {car.make_name} {car.model_name}
              </Anchor>
            ) : (
              <Text fw={500} fz="lg" c={textColor}>
                {car.make_name} {car.model_name}
              </Text>
            )}

            {/* Data rows */}
            <Stack gap={8}>
              <Group align="center" w="100%">
                <Text c="dimmed" fz="sm" style={{ flex: 1 }}>
                  {t("components.AuctionCarCard.lot")}
                </Text>
                <Text fz="sm">{car.lot === "0" ? "1" : car.lot}</Text>
              </Group>
              <Group align="center" w="100%">
                <Text c="dimmed" fz="sm" style={{ flex: 1 }}>
                  {t("components.AuctionCarCard.vin")}
                </Text>
                {car.vin && car.vin !== "-" ? (
                  hasLink ? (
                    <Anchor fz="sm" component={Link} to={to}>
                      {car.vin}
                    </Anchor>
                  ) : (
                    <Text fz="sm">{car.vin}</Text>
                  )
                ) : (
                  <Text fz="sm">{notAvailable}</Text>
                )}
              </Group>
              <Group align="center" w="100%">
                <Text c="dimmed" fz="sm" style={{ flex: 1 }}>
                  {t("components.AuctionCarCard.condition")}
                </Text>
                <Box style={{ flex: "0 0 auto" }}>
                  <Tooltip
                    label={String(t("tooltips.condition"))}
                    maw={300}
                    position="top-start"
                    offset={4}
                    openDelay={100}
                    withArrow
                    withinPortal
                    multiline
                    events={{ hover: true, focus: true, touch: true }}
                  >
                    {car?.condition ? renderCondition(car.condition) : null}
                  </Tooltip>
                </Box>
              </Group>
              <Group align="center" w="100%">
                <Text c="dimmed" fz="sm" style={{ flex: 1 }}>
                  {t("components.AuctionCarCard.estimate")}
                </Text>
                <Text fz="sm">{estimante}</Text>
              </Group>
              <Group align="center" w="100%">
                <Text c="dimmed" fz="sm" style={{ flex: 1 }}>
                  {t("components.AuctionCarCard.carburate_estimate")}
                </Text>
                <Text fz="sm" c={carburateEstimate !== "-" ? "success" : undefined}>
                  {carburateEstimate}
                </Text>
              </Group>
              <Group align="center" w="100%">
                <Text c="dimmed" fz="sm" style={{ flex: 1 }}>
                  {t("components.AuctionCarCard.hammer_price")}
                </Text>
                {car.hammer_price ? (
                  <Text fz="sm">{formatCurrency(car.hammer_price, currency.code)}</Text>
                ) : (
                  <Text fz="sm">{t("components.Auctions.notAvailable")}</Text>
                )}
              </Group>
              <Group align="center" w="100%">
                <Text c="dimmed" fz="sm" style={{ flex: 1 }}>
                  {t("components.AuctionCarCard.net_to_seller")}
                </Text>
                {car.net_to_seller ? (
                  <Text fz="sm">{formatCurrency(car.net_to_seller, currency.code)}</Text>
                ) : (
                  <Text fz="sm">{t("components.Auctions.notAvailable")}</Text>
                )}
              </Group>
            </Stack>

            {/* Description */}
            {car.description_summary && (
              <Stack gap="xs">
                <Text c="dimmed" fz="sm">
                  {t("components.AuctionCarCard.description_summary")}
                </Text>
                <Text fz="sm">{car.description_summary}</Text>
              </Stack>
            )}
          </Box>
          {/* Close button fixed to bottom of modal */}
          <Box style={{ flex: "0 0 auto" }}>
            <Button onClick={close} fullWidth>
              {t("common.close")}
            </Button>
          </Box>
        </Stack>
      </Modal>
    </Card>
  );
};
